import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import { ApiError } from 'api/core/ApiError';
import { OpenAPI } from 'api/core/OpenAPI';
import { request } from 'api/core/request';
import { DailyNavNoteAttachmentUpload } from 'api/models/DailyNavNoteAttachmentUpload';
import { DailyNavReportTemplateUploadResponse } from 'api/models/DailyNavReportTemplateUploadResponse';
import { DailyNavService } from 'api/services/DailyNavService';
import { FirmsService } from 'api/services/FirmsService';
import { DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import { clientWithAuth } from 'dashboard409a/services/base';
import {
  getBaseFileName,
  getSupportedFileType,
  getUploadKeyParts,
  uploadFileToSignedUrl,
} from 'services/hooks/documents/useFirmDocumentActions';

export const DAILY_NAV_VALIDATE_QUERY_KEY = 'DAILY_NAV_VALIDATE';
export const DAILY_NAV_REPORT_QUERY_KEY = 'DAILY_NAV_REPORT';
export const DAILY_NAV_REPORT_SOURCE_QUERY_KEY = 'DAILY_NAV_REPORT_SOURCE';

const errorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body) {
    const { detail } = error.body as Record<string, unknown>;
    if (typeof detail === 'string') {
      return detail;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

export const useGetDailyNavValidation = (firmId?: number, date?: string, enabled = true) =>
  useQuery({
    enabled: !isNil(firmId) && enabled,
    queryKey: [DAILY_NAV_VALIDATE_QUERY_KEY, firmId, date ?? 'effective-date'],
    queryFn: () => FirmsService.apiFirmsDailyNavValidateList(String(firmId), undefined, date),
    staleTime: 0,
  });

export const useGetDailyNavReport = (firmId?: number, date?: string, enabled = true) =>
  useQuery({
    enabled: !isNil(firmId) && enabled,
    queryKey: [DAILY_NAV_REPORT_QUERY_KEY, firmId, date ?? 'effective-date'],
    queryFn: () => FirmsService.apiFirmsDailyNavReportList(String(firmId), undefined, date),
    staleTime: 0,
  });

export const useGetDailyNavReportSource = (firmId?: number, date?: string, enabled = true) =>
  useQuery({
    enabled: !isNil(firmId) && enabled,
    queryKey: [DAILY_NAV_REPORT_SOURCE_QUERY_KEY, firmId, date ?? 'effective-date'],
    queryFn: () => FirmsService.apiFirmsDailyNavReportSourceList(String(firmId), undefined, date),
    staleTime: 0,
  });

export const useGenerateDailyNavReport = (firmId?: number) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ date }: { date?: string }) => FirmsService.apiFirmsDailyNavReportCreate(String(firmId), date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_QUERY_KEY, firmId] });
      enqueueSnackbar('Daily NAV report preview generated.', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to generate the Daily NAV report preview.'), { variant: 'error' });
    },
  });
};

export const useUnfinalizeDailyNavDay = (firmId?: number) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ date, reason }: { date?: string; reason: string }) =>
      FirmsService.apiFirmsDailyNavUnfinalizeCreate(String(firmId), { reason }, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_SOURCE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_VALIDATE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_QUERY_KEY] });
      enqueueSnackbar('Daily NAV day un-finalized.', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to un-finalize Daily NAV.'), { variant: 'error' });
    },
  });
};

export const useSendDailyNavDay = (firmId?: number) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ date }: { date?: string }) => FirmsService.apiFirmsDailyNavSendCreate(String(firmId), date),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_QUERY_KEY] });
      if (data.warnings?.length) {
        enqueueSnackbar(`Daily NAV report sent for ${data.date}. ${data.warnings.join(' ')}`, {
          variant: 'warning',
        });
        return;
      }
      const channels = [
        data.ftp_delivered ? 'SFTP delivery succeeded.' : null,
        data.email_delivered ? 'Email delivery succeeded.' : null,
      ].filter(Boolean);
      enqueueSnackbar(`Daily NAV report sent for ${data.date}. ${channels.join(' ')}`.trim(), {
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to send Daily NAV report.'), { variant: 'error' });
    },
  });
};

// Presigns and PUTs each file, returning what the note endpoints expect. The
// server files them under Daily NAV / <Company> / <the record's report date>.
const uploadDailyNavNoteFiles = async (recordId: number, files: File[]): Promise<DailyNavNoteAttachmentUpload[]> =>
  Promise.all(
    files.map(async file => {
      const fileType = getSupportedFileType(file);
      const { url, key } = await DailyNavService.apiDailyNavNotesAttachmentsUploadUrlList(String(recordId), fileType);
      await uploadFileToSignedUrl({ file, url });
      const { filePath, guid } = getUploadKeyParts(key);
      return { file_path: filePath, guid, file_type: fileType, filename: getBaseFileName(file, fileType) };
    })
  );

export const useCreateDailyNavNote = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ recordId, body, files = [] }: { recordId: number; body: string; files?: File[] }) =>
      DailyNavService.apiDailyNavNotesCreate(String(recordId), {
        body,
        attachments: await uploadDailyNavNoteFiles(recordId, files),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_SOURCE_QUERY_KEY] });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to add note.'), { variant: 'error' });
    },
  });
};

export const useDeleteDailyNavNote = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ recordId, noteId }: { recordId: number; noteId: number }) =>
      DailyNavService.apiDailyNavNotesDelete(String(recordId), String(noteId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_SOURCE_QUERY_KEY] });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to delete note.'), { variant: 'error' });
    },
  });
};

export const useDownloadDailyNavNoteAttachment = () => {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (attachmentId: number) =>
      DailyNavService.apiDailyNavNotesAttachmentsDownloadList(String(attachmentId)),
    onSuccess: ({ signed_url: signedUrl }) => {
      globalThis.open(signedUrl, '_blank', 'noopener');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to download document.'), { variant: 'error' });
    },
  });
};

export const useUploadDailyNavReportTemplate = () => {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ firmId, file }: { firmId: number; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return request<DailyNavReportTemplateUploadResponse>(OpenAPI, {
        method: 'POST',
        url: '/api/firms/{firm_id}/daily-nav/config/report-template/',
        path: { firm_id: String(firmId) },
        body: formData,
      });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorMessage(error, 'Failed to upload report template.'), { variant: 'error' });
    },
  });
};
